<template>
  <div :class="['max-w-xl mx-auto text-center space-y-6 md:space-y-10 p-6 md:p-16 border-4 transition-all duration-500', isEasy ? 'bg-amber-50/95 border-amber-800/50 sepia-shadow' : 'card-dark-rich border-amber-600/60 shadow-2xl shadow-black/40']" style="border-radius: 8px;">
    <div class="space-y-4">
      <Mascot size="w-16 h-16 md:w-48 md:h-48" :mood="stats.score > 500 ? 'happy' : 'neutral'" :message="getMascotMessage()" />
      <h2 :class="['text-4xl md:text-6xl font-serif-display text-shadow-warm transition-colors', isEasy ? 'ink-text' : 'text-amber-100']">Game <span :class="isEasy ? 'text-amber-800' : 'text-amber-500'">Over!</span></h2>
      <p :class="['font-serif-body font-semibold text-xs md:text-sm transition-colors italic', isEasy ? 'text-amber-800/70' : 'text-amber-300/70']" style="letter-spacing: 0.1em;">{{ isEasy ? 'Well played, little Nitwit' : 'The Blitz has ended' }}</p>
    </div>

    <div class="animate-in fade-in slide-in-from-top-4 duration-700 delay-300">
      <p :class="['text-xs md:text-sm font-serif-body font-bold mb-1 md:mb-2 uppercase cursor-default', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.1em;">Survival Time</p>
      <p :class="['text-xl md:text-2xl font-serif-body font-bold inline-block px-4 py-1 md:px-6 md:py-2 border-2 transition-colors hand-drawn-shadow cursor-default', isEasy ? 'text-blue-800 bg-blue-50 border-blue-700/50' : 'text-blue-300 bg-blue-900/20 border-blue-700/50']" style="border-radius: 6px;">
        {{ stats.duration }}<span class="text-xs md:text-sm ml-1">seconds</span>
      </p>
    </div>

    <!-- Passed Riddles Reveal Section -->
    <div v-if="stats.passedRiddles && stats.passedRiddles.length > 0 && !revealedRiddle" :class="['py-6 px-4 md:py-8 md:px-8 border-4 space-y-4 md:space-y-6 transition-all', isEasy ? 'bg-amber-100/30 border-amber-700/30' : 'bg-slate-900/30 border-slate-700/50']" style="border-radius: 8px;">
      <div class="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
        <Mascot size="w-16 h-16 md:w-24 md:h-24" mood="neutral" />
        <p :class="['font-serif-body font-semibold text-sm md:text-base italic', isEasy ? 'text-amber-900' : 'text-amber-100']">
          "I've written down the answers to {{ stats.passedRiddles.length === 1 ? 'this difficult riddle' : 'these ' + (stats.passedRiddles.length === 2 ? 'two' : 'three') + ' difficult riddles' }} that you passed during the game. Would you like to see one of them?"
        </p>
      </div>
      
      <div class="flex flex-wrap justify-center gap-3">
        <button
            v-for="(riddle, index) in stats.passedRiddles"
          :key="index"
          @click="revealRiddle(riddle)"
            :disabled="loadingReveal"
            :class="['group relative flex items-center justify-center w-12 h-16 md:w-16 md:h-24 border-2 transition-all transform hover:scale-105 hover:-rotate-3 hand-drawn-shadow', isEasy ? 'bg-blue-50 border-blue-700/40 text-blue-900' : 'bg-blue-900/40 border-blue-400/40 text-blue-200', loadingReveal ? 'opacity-50 cursor-not-allowed' : '']"
          style="border-radius: 4px;"
        >
          <div class="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full border border-black/20"></div>
          <span v-if="loadingReveal && pendingRiddle === riddle"
                class="font-serif-display text-lg md:text-2xl animate-pulse">...</span>
          <span v-else class="font-serif-display text-xl md:text-3xl opacity-60 group-hover:opacity-100">?</span>
        </button>
      </div>
    </div>

    <!-- Revealed Riddle Display -->
    <transition name="fade">
      <div v-if="revealedRiddle" :class="['p-6 md:p-10 border-4 space-y-4 md:space-y-6 transition-all animate-in fade-in zoom-in duration-500', isEasy ? 'bg-white/80 border-amber-800/40' : 'bg-stone-900/80 border-amber-600/40']" style="border-radius: 8px;">
        <h3 :class="['text-xs md:text-sm font-serif-body font-bold uppercase text-center', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.1em;">Revealed Secret</h3>
        <div class="space-y-4 text-center">
          <p :class="['text-lg md:text-2xl font-serif-display italic leading-tight', isEasy ? 'ink-text' : 'text-amber-100']">“{{ revealedRiddle.question }}”</p>
          <div class="inline-block px-6 py-2 border-2 border-dashed rounded-lg transition-colors" :class="isEasy ? 'border-amber-700/40 bg-amber-50' : 'border-amber-600/40 bg-stone-800'">
            <p :class="['text-xs md:text-sm font-serif-body font-bold uppercase mb-1', isEasy ? 'text-amber-900/60' : 'text-amber-400/60']">The Answer</p>
            <p :class="['text-2xl md:text-4xl font-serif-display font-bold', isEasy ? 'text-blue-800' : 'text-amber-400']">{{ revealedRiddle.answer }}</p>
          </div>
        </div>
      </div>
    </transition>

    <div :class="['py-6 md:py-12 border-y-4 space-y-6 md:space-y-8 transition-colors', isEasy ? 'border-amber-700/40' : 'border-slate-600/50']">
      <div class="flex justify-around items-center">
        <div>
          <p :class="['text-xs md:text-sm font-serif-body font-bold mb-1 md:mb-2 uppercase', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.1em;">Final Score</p>
          <p :class="['text-4xl md:text-7xl font-serif-display font-bold text-shadow-warm transition-colors', isEasy ? 'text-amber-800' : 'text-amber-100']">{{ stats.score }}</p>
        </div>
        <div v-if="stats.rank">
          <p :class="['text-xs md:text-sm font-serif-body font-bold mb-1 md:mb-2 uppercase', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.1em;">Rank</p>
          <p :class="['text-4xl md:text-7xl font-serif-display font-bold text-shadow-warm transition-colors', isEasy ? 'text-amber-800' : 'text-amber-100']">#{{ stats.rank }}</p>
        </div>
        <div v-else>
          <p :class="['text-xs md:text-sm font-serif-body font-bold mb-1 md:mb-2 uppercase', isEasy ? 'text-amber-900' : 'text-amber-300']"
             style="letter-spacing: 0.1em;">Rank</p>
          <p :class="['text-2xl md:text-4xl font-serif-display font-bold text-shadow-warm transition-colors', isEasy ? 'text-amber-800/50' : 'text-amber-100/50']">
            Unranked</p>
        </div>
      </div>
      
      <div class="pt-2 md:pt-4">
        <p :class="['text-xs md:text-sm font-serif-body font-bold mb-3 md:mb-4 uppercase', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.1em;">
          Top 10 {{ isEasy ? 'Easy' : 'Blitz' }} Nitwits
        </p>
        <div :class="['mx-auto max-w-xs space-y-2 p-3 md:p-4 border-2 transition-colors', isEasy ? 'bg-amber-100/50 border-amber-700/30' : 'bg-slate-900/40 border-slate-700/50']" style="border-radius: 8px;">
          <div v-for="(player, index) in leaderboard" :key="player.id"
            :class="['flex justify-between items-center font-serif-body',
              player.id === stats.sessionId ? (isEasy ? 'text-amber-900 font-bold' : 'text-amber-400 font-bold') : (isEasy ? 'text-amber-800/60' : 'text-amber-100/60')]">
            <span class="text-[10px] md:text-xs w-6 text-left">#{{ index + 1 }}</span>
            <span class="flex-grow text-left truncate px-2 text-xs md:text-base">{{ player.player_name }}</span>
            <span class="text-xs md:text-sm font-serif-display flex items-center gap-1">
              <span v-if="player.perfect_game" class="text-amber-400" title="Perfect Game">&#x2B50;</span>
              <span v-else-if="player.mastered_riddles" class="text-yellow-500" title="Riddle Master">&#x1F451;</span>
              {{ player.final_score }}
            </span>
          </div>

          <template v-if="stats.rank && stats.rank > leaderboard.length">
            <div :class="['py-1 text-base md:text-lg leading-none', isEasy ? 'text-amber-800/40' : 'text-amber-100/40']">...</div>
            <div :class="['flex justify-between items-center font-serif-body', isEasy ? 'text-amber-900 font-bold' : 'text-amber-400 font-bold']">
              <span class="text-[10px] md:text-xs w-6 text-left">#{{ stats.rank }}</span>
              <span class="flex-grow text-left truncate px-2 text-xs md:text-base">{{ stats.playerName }}</span>
              <span class="text-xs md:text-sm font-serif-display flex items-center gap-1">
                <span v-if="stats.perfectGame" class="text-amber-400" title="Perfect Game">&#x2B50;</span>
                <span v-else-if="stats.masteredRiddles" class="text-yellow-500" title="Riddle Master">&#x1F451;</span>
                {{ stats.score }}
              </span>
            </div>
          </template>
        </div>
      </div>

    </div>

    <!-- Account Creation Prompt (only for unauthenticated users with score > 0) -->
    <div v-if="!isAuthenticated && stats.score > 0 && !accountCreatedMessage"
         :class="['p-4 md:p-6 border-2 border-dashed transition-all',
                  isEasy ? 'border-amber-700/50 bg-amber-100/30' : 'border-amber-600/50 bg-slate-800/30']"
         style="border-radius: 8px;">
      <div class="flex flex-col md:flex-row items-center gap-4">
        <Mascot size="w-12 h-12" mood="thinking"/>
        <div class="flex-1 text-center md:text-left">
          <p :class="['font-serif-body font-semibold text-sm mb-1', isEasy ? 'text-amber-900' : 'text-amber-100']">
            Save your score forever!
          </p>
          <p :class="['font-serif-body text-xs', isEasy ? 'text-stone-600' : 'text-stone-400']">
            Create an account to protect your name and claim all your past games.
          </p>
        </div>
        <button
            @click="showAccountModal = true"
            :class="['px-4 py-2 font-serif-display font-bold border-2 transition-all hover:scale-105',
                   isEasy ? 'bg-amber-700 border-amber-900 text-white' : 'bg-amber-600 border-amber-800 text-white']"
            style="border-radius: 6px;"
        >
          Create Account
        </button>
      </div>
    </div>

    <!-- Account Created Success Message -->
    <div v-if="accountCreatedMessage"
         :class="['p-4 md:p-6 border-2 transition-all',
                  isEasy ? 'border-green-700/50 bg-green-100/30' : 'border-green-600/50 bg-green-900/30']"
         style="border-radius: 8px;">
      <div class="flex items-center gap-3 justify-center">
        <span class="text-xl">&#x2705;</span>
        <p :class="['font-serif-body font-semibold text-sm', isEasy ? 'text-green-800' : 'text-green-300']">
          {{ accountCreatedMessage }}
        </p>
      </div>
    </div>

    <div class="space-y-4 md:space-y-6">
      <button
        @click="$emit('restart')"
        :class="['w-full text-xl md:text-2xl font-serif-display font-bold py-4 md:py-6 transition-all transform hover:scale-[1.02] hand-drawn-shadow border-4', isEasy ? 'bg-amber-700 hover:bg-amber-600 border-amber-900 text-amber-50' : 'bg-amber-700 hover:bg-amber-500 border-amber-900 text-white']"
        style="border-radius: 8px; letter-spacing: 0.05em;"
      >
        Play Again
      </button>
      <p :class="['text-xs md:text-sm font-serif-body font-semibold italic transition-colors', isEasy ? 'text-stone-600' : 'text-stone-400']">Think you can beat that? Step up and try again!</p>
    </div>

    <!-- Auth Modal for Account Creation -->
    <AuthModal
        :visible="showAccountModal"
        :is-easy="isEasy"
        purpose="register"
        :player-name="stats.playerName"
        @close="showAccountModal = false"
        @success="handleAccountCreated"
    />
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import Mascot from './Mascot.vue'
import AuthModal from './AuthModal.vue'
import apiClient from '../api'
import {authService} from '../services/authService'

const props = defineProps({
  stats: {
    type: Object,
    required: true
  },
  gameMode: {
    type: String,
    default: 'blitz'
  }
})

const emit = defineEmits(['restart', 'show-ad'])

const isEasy = computed(() => props.gameMode === 'easy')
const leaderboard = ref([])
const revealedRiddle = ref(null)
const loadingReveal = ref(false)
const pendingRiddle = ref(null)

// Account creation
const showAccountModal = ref(false)
const isAuthenticated = computed(() => authService.isAuthenticated())
const accountCreatedMessage = ref('')

const handleAccountCreated = (result) => {
  showAccountModal.value = false
  if (result.claimed_sessions > 0) {
    accountCreatedMessage.value = `Account created! ${result.claimed_sessions} past game${result.claimed_sessions > 1 ? 's have' : ' has'} been linked to your account.`
  } else {
    accountCreatedMessage.value = 'Account created! Your score is now saved.'
  }
  // Refresh leaderboard to show updated data
  fetchLeaderboard()
}

const revealRiddle = (riddle) => {
  // Store the riddle we want to reveal
  pendingRiddle.value = riddle
  loadingReveal.value = true

  // Emit show-ad event with callback to reveal the riddle
  emit('show-ad', () => {
    revealedRiddle.value = pendingRiddle.value
    pendingRiddle.value = null
    loadingReveal.value = false
  })
}

const fetchLeaderboard = async () => {
  try {
    const res = await apiClient.get('/game/leaderboard/', {
      params: { mode: props.gameMode }
    })
    // Show top 10
    leaderboard.value = res.data.slice(0, 10)
  } catch (error) {
    console.error('Failed to fetch leaderboard:', error)
  }
}

onMounted(fetchLeaderboard)

const getMascotMessage = () => {
  if (props.stats.masteredRiddles) return "You are indeed a Master of Riddles!"
  if (props.stats.score === 0) return "Don't give up! Riddles can be tricky."
  if (props.stats.score < 500) return "Good effort! Keep practicing."
  if (props.stats.score < 2000) return "Impressive! You've got quite a wit."
  return "Incredible! You're a true Riddle Master!"
}
</script>
